import React, {useMemo} from 'react';

import {assertAndContinue} from 'Asserts';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {QUERY_LOADING_MESSAGE} from 'insights/InsightsConstants';
import BlankUniversalBlockView from 'insights/universal-blocks/BlankUniversalBlockView';
import {BlockViewContext} from 'insights/universal-blocks/BlockViewContext';
import PresentationalUniversalBlockView from 'insights/universal-blocks/PresentationalUniversalBlockView';
import {SharedUniversalBlockViewProps} from 'insights/universal-blocks/SharedUniversalBlockViewTypes';
import {RunQueryOutput} from 'insights/warehouse/AnalyticsWarehouseTypes';
import convertRunQueryOutputToLegacyOutput from 'insights/warehouse/convertRunQueryOutputToLegacyOutput';
import LoadingOverlay from 'ui-library/LoadingOverlay';

import {AnalyticsVisualization} from './__generated/AnalyticsVisualization';
import {AnalyticsVisualizationActions} from './AnalyticsVisualizationActions';
import getRegressionType from './data/getRegressionType';
import getVisualizationWarnings from './getVisualizationWarnings';

export function shouldRenderAnalyticsVisualizationEditor(
  props: Pick<AnalyticsVisualizationEditorProps, 'isRunning' | 'runQueryOutput'>
): boolean {
  return props.isRunning || props.runQueryOutput != null;
}

export interface Actions extends Pick<AnalyticsVisualizationActions, 'updateAnalyticsVisualization'> {
  cleanUpRegressionState: () => void;
}

export interface AnalyticsVisualizationEditorProps {
  actions: Actions;
  additionalActionButtons?: JSX.Element;
  isDisplayingSubQueryResults: boolean;
  isExpanded: boolean;
  isRunning: boolean;
  runQueryOutput: RunQueryOutput | null;
  visualization: AnalyticsVisualization;
  visualizationTitle: string;
}

export default function AnalyticsVisualizationEditor(
  props: AnalyticsVisualizationEditorProps
): JSX.Element | null {
  assertAndContinue(
    shouldRenderAnalyticsVisualizationEditor(props),
    'Caller should check shouldRenderAnalyticsVisualizationEditor'
  );

  const {viewStateId} = BlockViewContext.useContext();
  const legacyRunQueryOutput = useMemo(
    () => (props.runQueryOutput ? convertRunQueryOutputToLegacyOutput(props.runQueryOutput) : null),
    [props.runQueryOutput]
  );

  const warnings = useMemo(
    () =>
      legacyRunQueryOutput
        ? getVisualizationWarnings(legacyRunQueryOutput, props.isDisplayingSubQueryResults)
        : [],
    [props.isDisplayingSubQueryResults, legacyRunQueryOutput]
  );

  async function handleChangeAnalyticsVisualization(
    newAnalyticsVisualization: AnalyticsVisualization
  ): Promise<void> {
    const initialAnalyticsVisualization = props.visualization;
    await props.actions.updateAnalyticsVisualization(
      initialAnalyticsVisualization,
      newAnalyticsVisualization
    );

    if (
      getRegressionType(initialAnalyticsVisualization) != null &&
      getRegressionType(newAnalyticsVisualization) !== getRegressionType(initialAnalyticsVisualization)
    ) {
      props.actions.cleanUpRegressionState();
    }
  }

  function renderUniversalBlock(): JSX.Element {
    const runQueryOutput = props.runQueryOutput;

    if (runQueryOutput == null) {
      return <BlankUniversalBlockView {...sharedUniversalBlockViewProps} />;
    }

    return (
      <PresentationalUniversalBlockView
        actionOrigin={ActionOrigin.INSIGHTS}
        {...sharedUniversalBlockViewProps}
        configEditorSettings={{
          onChangeVisualizationConfig: handleChangeAnalyticsVisualization,
          showConfigEditor: true,
        }}
        inputDataFrame={runQueryOutput.dataFrame}
        isLegacyInsights={true}
        renderOptions={{
          isRawOutputEnabled: true,
        }}
        viewStateId={viewStateId}
        visualizationConfig={props.visualization}
      />
    );
  }

  const sharedUniversalBlockViewProps: SharedUniversalBlockViewProps = {
    paneProps: {
      title: props.visualizationTitle,
      toolbar: props.additionalActionButtons,
      warnings,
      dataTestAttrs: {component: 'AnalyticsVisualizationEditor', element: 'root'},
    },
  };

  return (
    <LoadingOverlay message={QUERY_LOADING_MESSAGE} isLoading={props.isRunning || !props.runQueryOutput}>
      {renderUniversalBlock()}
    </LoadingOverlay>
  );
}
